import { ReactElement, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import SignalWifiStatusbarConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4';
import { useTranslation } from 'react-i18next';

import {
  MESSAGE_ERROR_CONNECTION,
  useGlobalContext
} from 'context/GlobalContext';
import { AccessElements } from 'helpers/app.config';
import { FullScreenLoader } from 'components/shared';

import LoadingFallback from './LoadingFallback';

type AuthPageProps = {
  permissions?: AccessElements[];
  element: ReactElement;
};

/**
 * AuthPage Component
 *
 * Renders the authentication page based on user authentication status and permissions.
 *
 * @component
 * @param {Object} AuthPageProps - The properties for the AuthPage component.
 * @param {ReactElement} AuthPageProps.element The element to render if user is authenticated and has required permissions.
 * @param {Array} [AuthPageProps.permissions=[AccessElements.ClientCreate]] The permissions required to access the page.
 * @returns {ReactElement} The rendered component.
 * @example
 * <AuthPage permissions={[AccessElements.ClientCreate]} element={<YourComponent />} />
 */

function AuthPage({ element, permissions }: AuthPageProps): ReactElement {
  const isAuthenticated = useIsAuthenticated();
  const { userData, hasPerm, isLoadingPermissions } = useGlobalContext();
  const { showError } = useGlobalContext();
  const { instance } = useMsal();
  const { t } = useTranslation('global');

  if (!userData || !isAuthenticated || isLoadingPermissions) {
    return <FullScreenLoader />;
  }

  if (hasPerm(permissions) && userData?.extension_GSerUserEmail !== '') {
    if (!navigator.onLine) {
      showError(t(MESSAGE_ERROR_CONNECTION), true);
      return (
        <FullScreenLoader customStyle={{ color: '#fff' }}>
          <SignalWifiStatusbarConnectedNoInternet4Icon />
        </FullScreenLoader>
      );
    }
    return <Suspense fallback={<LoadingFallback />}>{element}</Suspense>;
  }

  if (userData?.extension_GSerUserEmail === '') {
    return <>{instance.logout()}</>;
  }

  return <Navigate to="/forbidden" />;
}

export default AuthPage;
